import { useCallback, useEffect, useState } from "react";
import { api, type Device, type DeviceInput } from "../api";
import { subscribeForeground } from "../lib/page-lifecycle";

export function useDevices() {
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const data = await api<{ devices: Device[] }>("/api/devices");
      setDevices(data.devices);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load devices");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const t = setInterval(() => void refresh(), 15000);
    const stop = subscribeForeground(() => {
      void refresh();
    });
    return () => {
      clearInterval(t);
      stop();
    };
  }, [refresh]);

  const createDevice = useCallback(
    async (input: DeviceInput) => {
      const created = await api<Device>("/api/devices", { method: "POST", body: JSON.stringify(input) });
      await refresh();
      return created;
    },
    [refresh],
  );

  const updateDevice = useCallback(
    async (id: string, input: Partial<DeviceInput>) => {
      const updated = await api<Device>(`/api/devices/${id}`, { method: "PATCH", body: JSON.stringify(input) });
      await refresh();
      return updated;
    },
    [refresh],
  );

  const deleteDevice = useCallback(
    async (id: string) => {
      await api(`/api/devices/${id}`, { method: "DELETE" });
      await refresh();
    },
    [refresh],
  );

  return { devices, loading, error, refresh, createDevice, updateDevice, deleteDevice };
}
